import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Star, RefreshCw, AlertCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Testimonial, TestimonialStatus, GetTestimonialsParams } from '../../services/admin/adminService';
import DataTable, { Column } from '../../components/common/DataTable';

const TestimonialsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState({
    pending: 0,
    approved: 0,
    featured: 0,
    total: 0
  });
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; testimonial: Testimonial | null }>({
    show: false,
    testimonial: null
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: GetTestimonialsParams = {
        page: 1,
        limit: 100, // Fetch enough data for client-side pagination
      };

      const data = await adminService.getAllTestimonials(params);
      setTestimonials(data.testimonials);
      setStatistics(data.statistics);

      if (showToast) {
        toast.success('Testimonials refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch testimonials');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteTestimonial = async () => {
    if (!deleteModal.testimonial) return;

    try {
      setDeleting(true);
      await adminService.deleteTestimonial(deleteModal.testimonial.id);
      toast.success('Testimonial deleted successfully!');
      setDeleteModal({ show: false, testimonial: null });
      fetchTestimonials();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete testimonial');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewTestimonial = (testimonial: Testimonial) => {
    navigate(`/admin/testimonials/${testimonial.id}`);
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
        <span className="ml-2 text-sm text-secondary-600">({rating})</span>
      </div>
    );
  };

  if (loading && testimonials.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Testimonials Management</h1>
          <p className="text-secondary-600 mt-1">Manage customer testimonials and reviews</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchTestimonials(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/admin/testimonials/create')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Create Testimonial
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Total Testimonials</div>
          <div className="text-2xl font-bold text-secondary-900 mt-1">{statistics.total}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{statistics.pending}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Approved</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{statistics.approved}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Featured</div>
          <div className="text-2xl font-bold text-primary-600 mt-1">{statistics.featured}</div>
        </div>
      </div>

      {/* Testimonials Table */}
      <DataTable
        data={testimonials}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search testimonials by name or text..."
        searchFields={['name', 'text']}
        filterable={true}
        filterOptions={[
          { label: 'All', value: 'all', field: 'status' },
          { label: 'Pending', value: 'pending', field: 'status' },
          { label: 'Approved', value: 'approved', field: 'status' },
          { label: 'Rejected', value: 'rejected', field: 'status' },
          { label: 'Featured', value: 'featured', field: 'featured' },
        ]}
        defaultPageSize={10}
        emptyMessage="No testimonials found"
        emptyIcon={<Star size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: 'Name',
            accessor: (testimonial) => (
              <div className="flex items-center gap-3">
                {testimonial.profile ? (
                  <img
                    src={testimonial.profile}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      // Fallback to letter avatar if image fails to load
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center"
                  style={{ display: testimonial.profile ? 'none' : 'flex' }}
                >
                  <span className="text-primary-600 font-semibold text-sm">
                    {testimonial.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-secondary-900">
                    {testimonial.name}
                  </h3>
                  {testimonial.location && (
                    <p className="text-xs text-secondary-500">{testimonial.location}</p>
                  )}
                </div>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Testimonial',
            accessor: (testimonial) => (
              <p className="text-sm text-secondary-600 max-w-md line-clamp-2">
                {testimonial.text}
              </p>
            ),
          },
          {
            header: 'Rating',
            accessor: (testimonial) => renderStars(testimonial.rating),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Status',
            accessor: (testimonial) => (
              <span
                className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${testimonial.status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : testimonial.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                  }`}
              >
                {testimonial.status.charAt(0).toUpperCase() + testimonial.status.slice(1)}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Featured',
            accessor: (testimonial) => {
              const isFeatured = Boolean(testimonial.featured);
              return (
                <span
                  className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${isFeatured
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}
                >
                  {isFeatured ? 'Yes' : 'No'}
                </span>
              );
            },
            align: 'center',
            sortable: true,
          },
          {
            header: 'Date',
            accessor: (testimonial) => (
              <span className="text-sm text-secondary-600">
                {new Date(testimonial.created_at).toLocaleDateString()}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(testimonial) => (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewTestimonial(testimonial);
              }}
              className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-colors"
              title="View Testimonial"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/testimonials/${testimonial.id}/edit`);
              }}
              className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
              title="Edit Testimonial"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ show: true, testimonial });
              }}
              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
              title="Delete Testimonial"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
        onRowClick={handleViewTestimonial}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.testimonial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Testimonial</h3>
              </div>

              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the testimonial from{' '}
                <strong>{deleteModal.testimonial.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteTestimonial}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal({ show: false, testimonial: null })}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
